import {
  TranscriptionDocument,
  TranscriptionDocumentResult,
  TranscriptionDocumentType
} from '@/types/transcriptionDocumentTypes';
import { uploadFileToBlob } from './directBlobUpload';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;
type GetAccessTokenWithPopupType = (
  options?: any
) => Promise<string | undefined>;

/**
 * Helper function to acquire Auth0 access token.
 * Tries silently, then falls back to popup.
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn(
      'Silent token acquisition failed for TranscriptionDocumentService:',
      error
    );
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      console.log(
        'Falling back to interactive token acquisition for TranscriptionDocumentService...'
      );
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error(
          'Interactive token acquisition failed to return a token for TranscriptionDocumentService.'
        );
      }
      return token;
    }
    throw error; // Rethrow other errors
  }
};

/**
 * Get all documents for a transcription session
 */
export const getSessionDocuments = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TranscriptionDocument[]> => {
  try {
    const accessToken = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );
    const response = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/documents`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.documents || [];
  } catch (error) {
    console.error('Error fetching session documents:', error);
    return [];
  }
};

/**
 * Get documents by type for a session
 */
export const getSessionDocumentsByType = async (
  sessionId: string,
  documentType: TranscriptionDocumentType,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TranscriptionDocument[]> => {
  try {
    const accessToken = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );
    const response = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/documents?type=${documentType}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.documents || [];
  } catch (error) {
    console.error('Error fetching session documents by type:', error);
    return [];
  }
};

/**
 * Upload audio file and create document record
 */
export const uploadAudioDocument = async (
  sessionId: string,
  audioBlob: Blob,
  documentName: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  fallbackDurationSeconds?: number,
  options?: { recordingId?: string }
): Promise<TranscriptionDocumentResult> => {
  void fallbackDurationSeconds;
  void options;

  const file = new File([audioBlob], documentName, {
    type: audioBlob.type || 'audio/webm'
  });

  return uploadGenericDocument(
    sessionId,
    file,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    {
      documentType: TranscriptionDocumentType.OriginalAudio,
      version: 'original',
      skipAnalysis: false
    }
  );
};

/**
 * Upload a generic document (e.g., PDF, DOCX, image) and create a document record
 */
type DirectDocumentUploadInitResponse = {
  documentId: string;
  uploadUrl: string;
  blobUrl: string;
  blobName: string;
};

const isAudioOrVideoFile = (fileName: string): boolean => {
  const mediaExtensions = [
    '.mp3',
    '.wav',
    '.m4a',
    '.webm',
    '.mp4',
    '.ogg',
    '.flac',
    '.aac',
    '.mov',
    '.avi',
    '.mkv'
  ];
  return mediaExtensions.some((ext) => fileName.toLowerCase().endsWith(ext));
};

export const uploadGenericDocument = async (
  sessionId: string,
  file: File,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  options?: {
    description?: string;
    documentType?: TranscriptionDocumentType;
    version?: string;
    skipAnalysis?: boolean;
    abortSignal?: AbortSignal;
    onProgress?: (progress: number) => void;
  }
): Promise<TranscriptionDocumentResult> => {
  try {
    const finalDocumentType =
      options?.documentType ??
      (isAudioOrVideoFile(file.name)
        ? TranscriptionDocumentType.OriginalAudio
        : undefined);

    const accessToken = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const initPayload = {
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      fileSizeBytes: file.size,
      description: options?.description,
      documentType: finalDocumentType,
      version: options?.version
    };

    const initResponse = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/documents/upload-init`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(initPayload)
      }
    );

    if (!initResponse.ok) {
      const errorText = await initResponse.text();
      throw new Error(`Upload initialization failed: ${errorText}`);
    }

    const initData = (await initResponse.json()) as {
      upload: DirectDocumentUploadInitResponse;
    };

    await uploadFileToBlob(initData.upload.uploadUrl, file, {
      abortSignal: options?.abortSignal,
      onProgress: options?.onProgress
    });

    const completePayload = {
      documentId: initData.upload.documentId,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      fileSizeBytes: file.size,
      description: options?.description,
      documentType: finalDocumentType,
      version: options?.version,
      skipAnalysis: options?.skipAnalysis ?? false
    };

    const completeResponse = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/documents/upload-complete`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(completePayload)
      }
    );

    if (!completeResponse.ok) {
      const errorText = await completeResponse.text();
      throw new Error(`Upload completion failed: ${errorText}`);
    }

    const data = await completeResponse.json();

    if (options?.onProgress) {
      options.onProgress(100);
    }

    return {
      success: true,
      document: data.document,
      message: data.message
    };
  } catch (error) {
    console.error('Error uploading generic document:', error);
    return {
      success: false,
      message:
        error instanceof Error ? error.message : 'Failed to upload document'
    };
  }
};

export const processSessionDocuments = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<void> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(
    `${API_BASE_URL}/transcriptionsessions/${sessionId}/process-documents`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'X-User-Id': userId
      }
    }
  );

  if (!(response.status === 202 || response.ok)) {
    const errorData = await response.json().catch(() => null);
    throw new Error(
      errorData?.message ||
        `Failed to start session document processing: ${response.statusText}`
    );
  }
};

/**
 * Download a document via authenticated API
 */
export const downloadDocument = async (
  sessionId: string,
  documentId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  downloadFileName?: string
): Promise<boolean> => {
  try {
    const accessToken = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );
    const response = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/download-document/${documentId}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadFileName || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Error downloading document:', error);
    return false;
  }
};

export const createDocumentPreviewUrl = async (
  sessionId: string,
  documentId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<{ url: string; contentType: string }> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const response = await fetch(
    `${API_BASE_URL}/transcriptionsessions/${sessionId}/download-document/${documentId}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'X-User-Id': userId
      }
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const blob = await response.blob();
  return {
    url: window.URL.createObjectURL(blob),
    contentType: response.headers.get('content-type') || blob.type
  };
};

/**
 * Delete a document from a transcription session
 */
export const deleteDocument = async (
  sessionId: string,
  documentId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<{ success: boolean; message: string }> => {
  try {
    const accessToken = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );
    const response = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/documents/${documentId}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId
        }
      }
    );

    if (!response.ok) {
      if (response.status === 400) {
        const data = await response.json();
        return {
          success: false,
          message: data.message || 'Cannot delete this document'
        };
      }
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      message: data.message || 'Document deleted successfully'
    };
  } catch (error) {
    console.error('Error deleting document:', error);
    return {
      success: false,
      message: 'Failed to delete document'
    };
  }
};

/**
 * Set a document as the primary audio document for a recording
 * @param sessionId - The transcription session ID
 * @param recordingId - The recording ID to attach the document to
 * @param documentId - The document ID to set as primary
 */
export const setPrimaryAudioDocumentForRecording = async (
  sessionId: string,
  recordingId: string,
  documentId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<boolean> => {
  try {
    const accessToken = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );
    const response = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/recordings/${recordingId}/set-primary-audio/${documentId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId
        },
        body: JSON.stringify({})
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.success;
  } catch (error) {
    console.error('Error setting primary audio document for recording:', error);
    return false;
  }
};

/**
 * @deprecated Use setPrimaryAudioDocumentForRecording instead
 * This function is kept for backward compatibility but the backend endpoint has changed
 */
export const setPrimaryAudioDocument = setPrimaryAudioDocumentForRecording;

/**
 * Create a batch transcript document
 */
export const createBatchTranscriptDocument = async (
  sessionId: string,
  transcriptContent: string,
  processingEngine: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  quality?: number
): Promise<TranscriptionDocumentResult> => {
  try {
    const accessToken = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );
    const response = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/create-batch-transcript`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId
        },
        body: JSON.stringify({
          transcriptContent,
          processingEngine,
          quality
        })
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      document: data.document,
      message: data.message
    };
  } catch (error) {
    console.error('Error creating batch transcript document:', error);
    return {
      success: false,
      message: 'Failed to create batch transcript document'
    };
  }
};

/**
 * Generate and download a formatted transcript document
 */
export const generateAndDownloadFormattedTranscript = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<{ success: boolean; message?: string }> => {
  try {
    const accessToken = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    // Step 1: Generate the formatted transcript document
    const generateResponse = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/generate-formatted-transcript`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId
        }
      }
    );

    if (!generateResponse.ok) {
      throw new Error(
        `Failed to generate transcript: HTTP ${generateResponse.status}`
      );
    }

    const generateData = await generateResponse.json();
    if (!generateData.success) {
      throw new Error(
        generateData.message || 'Failed to generate formatted transcript'
      );
    }

    const documentId = generateData.document.id;

    // Step 2: Download the generated document
    const downloadResponse = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/download-formatted-transcript/${documentId}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId
        }
      }
    );

    if (!downloadResponse.ok) {
      throw new Error(
        `Failed to download transcript: HTTP ${downloadResponse.status}`
      );
    }

    // Create download link and trigger download
    const blob = await downloadResponse.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      generateData.document.documentName || `transcript-${sessionId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      message: 'Formatted transcript downloaded successfully'
    };
  } catch (error) {
    console.error(
      'Error generating and downloading formatted transcript:',
      error
    );
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : 'Failed to download formatted transcript'
    };
  }
};
